import { estimateTokenCostUsd } from "../../lib/connectorUsage";
import type { AiUsageRecord } from "../../../shared/aiUsageRecords";

export type {
  AiSpendRecord,
  AiUsageRecord,
} from "../../../shared/aiUsageRecords";

export type ModelUsageRow = {
  model: string;
  provider: string;
  aiProviderId: string | null;
  connectorId: string | null;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheReadTokens: number;
  requestCount: number;
  estimatedCostUsd: number | null;
};

export type AiUsageSummary = {
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cacheReadTokens: number;
  requestCount: number;
  estimatedCostUsd: number | null;
  modelCount: number;
};

type TokenUsage = {
  connectorId: string | null;
  model: string;
  inputTokens: number | null;
  outputTokens: number | null;
  cacheReadTokens: number | null;
  cacheWriteTokens: number | null;
};

function recordTokens(record: TokenUsage) {
  return {
    inputTokens: record.inputTokens ?? 0,
    outputTokens: record.outputTokens ?? 0,
    cacheReadTokens: record.cacheReadTokens ?? 0,
    cacheWriteTokens: record.cacheWriteTokens ?? 0,
  };
}

export function recordCost(
  record: TokenUsage,
  prices?: ReadonlyMap<string, number>
): number | null {
  if (!prices || !record.connectorId || !record.model) return null;
  return estimateTokenCostUsd(
    record.connectorId,
    record.model,
    recordTokens(record),
    prices
  );
}

export function computeAiUsageSummary(
  records: readonly AiUsageRecord[],
  prices?: ReadonlyMap<string, number>
): AiUsageSummary {
  const models = new Set<string>();
  let inputTokens = 0;
  let outputTokens = 0;
  let cacheReadTokens = 0;
  let requestCount = 0;
  let estimatedCostUsd = 0;
  let hasAnyCost = false;

  for (const record of records) {
    requestCount += record.requestCount ?? 0;
    // Request-only rows (the connector `requests` measure) carry no model or
    // tokens, so they contribute turns but nothing to the token/cost totals.
    if (!record.model) continue;

    const tokens = recordTokens(record);
    inputTokens += tokens.inputTokens;
    outputTokens += tokens.outputTokens;
    cacheReadTokens += tokens.cacheReadTokens;
    models.add(record.model);

    const cost = recordCost(record, prices);
    if (cost != null) {
      estimatedCostUsd += cost;
      hasAnyCost = true;
    }
  }

  return {
    inputTokens,
    outputTokens,
    totalTokens: inputTokens + outputTokens,
    cacheReadTokens,
    requestCount,
    estimatedCostUsd: hasAnyCost ? estimatedCostUsd : null,
    modelCount: models.size,
  };
}

export function computeModelUsageRows(
  records: readonly AiUsageRecord[],
  prices?: ReadonlyMap<string, number>
): ModelUsageRow[] {
  const byModel = new Map<string, ModelUsageRow>();

  for (const record of records) {
    if (!record.model) continue;
    // Key by connector, not provider type, so distinct accounts sharing a type
    // don't merge and each row matches its connector's usage modal.
    const key = `${record.connectorId ?? ""}\0${record.model}`;
    const tokens = recordTokens(record);
    const existing = byModel.get(key);
    if (existing) {
      existing.inputTokens += tokens.inputTokens;
      existing.outputTokens += tokens.outputTokens;
      existing.totalTokens += tokens.inputTokens + tokens.outputTokens;
      existing.cacheReadTokens += tokens.cacheReadTokens;
      existing.requestCount += record.requestCount ?? 0;
      const cost = recordCost(record, prices);
      if (cost != null) {
        existing.estimatedCostUsd = (existing.estimatedCostUsd ?? 0) + cost;
      }
      continue;
    }

    const cost = recordCost(record, prices);
    byModel.set(key, {
      model: record.model,
      provider: record.provider,
      aiProviderId: record.aiProviderId ?? null,
      connectorId: record.connectorId ?? null,
      inputTokens: tokens.inputTokens,
      outputTokens: tokens.outputTokens,
      totalTokens: tokens.inputTokens + tokens.outputTokens,
      cacheReadTokens: tokens.cacheReadTokens,
      requestCount: record.requestCount ?? 0,
      estimatedCostUsd: cost,
    });
  }

  return [...byModel.values()].sort(
    (a, b) => b.totalTokens - a.totalTokens || a.model.localeCompare(b.model)
  );
}

const compactTokenFormatter = new Intl.NumberFormat("en-US", {
  notation: "compact",
  maximumFractionDigits: 1,
});

export function formatTokenCount(count: number): string {
  if (count < 10_000) return count.toLocaleString("en-US");
  return compactTokenFormatter.format(count);
}

export function formatCacheRatio(ratio: number | null): string {
  return ratio == null ? "—" : `${Math.round(ratio * 100)}%`;
}

export function formatCacheHitRate(
  cacheReadTokens: number,
  totalInputTokens: number
): string {
  return formatCacheRatio(
    totalInputTokens === 0 ? null : cacheReadTokens / totalInputTokens
  );
}

export function formatEstimatedCostUsd(amount: number | null): string {
  if (amount == null) return "—";
  if (amount >= 100)
    return `$${amount.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  if (amount >= 10)
    return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
  return `$${amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
